import { useMutation, useQueryClient } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import type { BookingInvitationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** The respond payload — which invitation (scoped by its facility + booking) and
 *  the accept/decline decision. */
export interface RespondBookingInvitationVars {
  facilityId: string;
  bookingId: string;
  invitationId: string;
  accept: boolean;
}

/**
 * Accept or decline a court-booking invitation — the mutation behind the
 * BookingInvitesInboxPage rows. Accept → POST .../invitations/{id}/accept (adds the
 * caller to that booking's roster); decline → POST .../invitations/{id}/decline.
 *
 * On SUCCESS it invalidates the cache slots a respond can change so each re-reads
 * the server's truth — never an optimistic local edit:
 *   - booking.myInvitations()        — the inbox: the responded invite leaves the
 *                                       pending list (both accept AND decline).
 * On ACCEPT it also invalidates the roster, detail, every facility-scoped My
 * bookings list, the cross-facility list, and Play's next-up read. Acceptance
 * makes the reservation durably reachable after the pending invitation leaves
 * the inbox; decline does not create that access.
 * It does NOT swallow errors — a 409 / 404 / 403 surfaces on `mutation.error` for
 * the page to map to honest inline copy.
 */
export function useRespondBookingInvitation() {
  const queryClient = useQueryClient();

  return useMutation<
    BookingInvitationResponse,
    unknown,
    RespondBookingInvitationVars
  >({
    mutationFn: ({
      facilityId,
      bookingId,
      invitationId,
      accept,
    }: RespondBookingInvitationVars) =>
      accept
        ? facilityApi.acceptBookingInvitation(
            facilityId,
            bookingId,
            invitationId,
          )
        : facilityApi.declineBookingInvitation(
            facilityId,
            bookingId,
            invitationId,
          ),
    onSuccess: (_data, vars) => {
      // The inbox refetches so the responded invite leaves the pending list.
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.myInvitations(),
      });
      if (!vars.accept) return;

      // An accept adds the caller to the booking's roster → it must re-read.
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.participants(
          vars.facilityId,
          vars.bookingId,
        ),
      });
      // The booking-detail hub reflects the new participant / state.
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.detail(vars.bookingId),
      });
      // The accepted booking is now part of both participant-visible list reads.
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.myBookingsPrefix(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.allBookings(),
      });
      // Play's next-up card is another consumer of the cross-facility booking
      // truth; it must not keep showing the pre-accept result.
      queryClient.invalidateQueries({
        queryKey: queryKeys.play.myUpcoming(),
      });
    },
  });
}
